import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { useEffect, useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useHeaderActions from 'app/hooks/useHeaderActions';
import useHeaderTitle from 'app/hooks/useHeaderTitle';
import useNavigationHeader from 'app/hooks/useNavigationHeader';
import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';
import ErrorState from 'app/shared-components/common/ErrorState';

import { useGetWebsiteTheme, type WebsiteThemeFavicon } from '../hooks/useGetWebsiteTheme';
import { useUpdateWebsiteFavicon } from './hooks/useUpdateWebsiteFavicon';
import { ImageCropModal } from '../../Content/components/ImageCropModal';
import { faviconSize, getPrimaryFavicon, sortFaviconsBySizeDesc } from '../favicons';

const MAX_PREVIEW_PX = 96;
const PREVIEW_VARIANT_SIZES = [192, 48, 32, 16];

function FaviconVariants({ favicons }: { favicons: WebsiteThemeFavicon[] }): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:flex-wrap vtwo:items-end vtwo:gap-8">
      {sortFaviconsBySizeDesc(favicons).map((favicon) => {
        const size = Math.min(faviconSize(favicon.variant) || MAX_PREVIEW_PX, MAX_PREVIEW_PX);
        return (
          <Box
            key={favicon.variant}
            className="vtwo:flex vtwo:flex-col vtwo:items-center vtwo:gap-2"
          >
            <img
              src={favicon.url}
              alt={`Favicon ${favicon.variant}`}
              style={{ width: size, height: size }}
              className="vtwo:object-contain"
            />
            <Typography className="vtwo:text-sm vtwo:text-typography-secondary">
              {favicon.variant.replace(/x/i, '×')}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

const FaviconEditPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';
  const organisationId = searchParams.get('organisationId') ?? '';

  const { theme, isLoading, isError, error, refetch } = useGetWebsiteTheme({ websiteId });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewedUrl, setPreviewedUrl] = useState<string | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState('');
  const [cropKey, setCropKey] = useState(0);

  const { mutate: updateFavicon, isLoading: isSaving } = useUpdateWebsiteFavicon();

  const themePath = `/plus-websites/theme-and-styles?${new URLSearchParams({ organisationId })}`;

  function handlePreview() {
    if (!selectedFile) return;
    updateFavicon(
      { websiteId, file: selectedFile },
      {
        onSuccess: () => {
          // Show the sizes generated from the chosen icon straight away; a real
          // refetch below swaps in the backend variants once they are available.
          setPreviewedUrl(URL.createObjectURL(selectedFile));
          refetch();
        },
      }
    );
  }

  function handleSave() {
    if (!selectedFile) return;
    updateFavicon({ websiteId, file: selectedFile }, { onSuccess: () => navigate(themePath) });
  }

  useNavigationHeader({ title: 'Back', navigateTo: themePath });
  useHeaderTitle('Favicon');
  useHeaderActions([
    {
      title: 'Preview',
      type: 'button',
      callback: handlePreview,
      disabled: !selectedFile || isSaving,
    },
    { title: 'Save', type: 'button', callback: handleSave, disabled: !selectedFile || isSaving },
  ]);

  // Release the object URL when it is replaced or the page unmounts.
  useEffect(() => () => URL.revokeObjectURL(previewUrl ?? ''), [previewUrl]);
  useEffect(() => () => URL.revokeObjectURL(previewedUrl ?? ''), [previewedUrl]);

  if (!websiteId) {
    return (
      <ErrorState
        title="Missing required parameters"
        subtitle="Cannot edit the favicon without a valid website."
      />
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Failed to load theme"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  if (isLoading || !theme) {
    return (
      <MarketingStationLayout className="vtwo:flex vtwo:h-full vtwo:items-center vtwo:justify-center">
        <CircularProgress size={24} />
      </MarketingStationLayout>
    );
  }

  const iconSrc = previewUrl ?? getPrimaryFavicon(theme.favicons)?.url;

  let previewFavicons: WebsiteThemeFavicon[] = theme.favicons;
  if (previewFavicons.length === 0 && previewedUrl) {
    previewFavicons = PREVIEW_VARIANT_SIZES.map((size) => ({
      url: previewedUrl,
      variant: `${size}x${size}`,
    }));
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so re-selecting the same file still fires a change event.
    event.target.value = '';
    if (!file) return;
    setCropFileName(file.name);
    setCropKey((key) => key + 1);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setCropImageSrc(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCropDone = (file: File) => {
    setCropImageSrc(null);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  return (
    <MarketingStationLayout className="vtwo:flex vtwo:flex-col vtwo:h-full vtwo:overflow-hidden">
      <Box className="vtwo:flex-1 vtwo:bg-plus-canvas vtwo:overflow-y-auto">
        <Box className="vtwo:max-w-3xl vtwo:mx-auto vtwo:py-12 vtwo:px-8">
          <Box className="vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white vtwo:p-8">
            <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary vtwo:mb-6">
              Favicon
            </Typography>

            <Typography className="vtwo:text-typography-primary vtwo:mb-2">Icon</Typography>
            <Box className="vtwo:flex vtwo:items-center vtwo:justify-center vtwo:rounded-lg vtwo:border vtwo:border-plus-border vtwo:bg-white vtwo:py-8 vtwo:mb-4">
              {iconSrc && (
                <img
                  src={iconSrc}
                  alt="Website favicon"
                  className="vtwo:h-12 vtwo:object-contain"
                />
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="vtwo:hidden"
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              className="vtwo:text-[#374151] vtwo:border-[#D1D5DB] vtwo:hover:border-[#9CA3AF] vtwo:hover:bg-black/[0.02]"
            >
              Change icon
            </Button>

            {previewFavicons.length > 0 && (
              <>
                <Divider className="vtwo:my-8" />
                <Typography className="vtwo:text-typography-primary vtwo:mb-4">
                  This is how yours key elements will looks like:
                </Typography>
                <FaviconVariants favicons={previewFavicons} />
              </>
            )}
          </Box>
        </Box>
      </Box>
      <ImageCropModal
        key={cropKey}
        open={Boolean(cropImageSrc)}
        imageSrc={cropImageSrc ?? ''}
        aspectRatio="1:1"
        defaultFileName={cropFileName}
        confirmLabel="Crop"
        onClose={() => setCropImageSrc(null)}
        onCropDone={handleCropDone}
      />
    </MarketingStationLayout>
  );
};

export default FaviconEditPage;
